/** The Customer section of the "New booking" form: full name (optional) +
 *  tickets stepper + email (required). No phone/consent fields — see
 *  `CreateBookingModalHelpers.ts` / the BON-142 route header for why. */
import { Minus, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { inputClass, labelClass } from './CreateBookingModalHelpers';

export interface CustomerFieldsProps {
  fullName: string;
  setFullName: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  tickets: number;
  setTickets: (v: number) => void;
  setAllTickets: () => void;
  /** Seats-left bound, or null when no workshop is picked yet. */
  ticketsMax: number | null;
}

/** Click-to-edit ticket count (BON-142): the number between −/+ is a button
 *  that swaps to an inline numeric input on click. Enter/blur parses + clamps
 *  via `setTickets` (which already clamps to `[1, seatsLeft]`), Escape reverts
 *  without committing. */
function TicketCountInput({
  tickets,
  setTickets,
  max,
}: {
  tickets: number;
  setTickets: (v: number) => void;
  max: number | null;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(tickets));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(String(tickets));
  }, [tickets, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  if (!max) {
    return (
      <span className="w-6 text-center text-sm font-bold text-bark-900 dark:text-stone-100">
        {tickets}
      </span>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        aria-label="Edit ticket count"
        className="w-6 rounded text-center text-sm font-bold text-bark-900 hover:bg-stone-100 dark:text-stone-100 dark:hover:bg-[#252b26]"
      >
        {tickets}
      </button>
    );
  }

  const commit = () => {
    setTickets(Number.parseInt(draft, 10));
    setEditing(false);
  };

  return (
    <input
      ref={inputRef}
      type="number"
      min={1}
      max={max}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
        if (e.key === 'Escape') setEditing(false);
      }}
      aria-label="Tickets"
      autoFocus
      className="w-14 rounded-md border border-stone-300 px-1 py-0.5 text-center text-sm font-bold text-bark-900 focus:outline-none focus:ring-2 focus:ring-moss-500 dark:border-stone-600 dark:bg-[#1e2520] dark:text-stone-100"
    />
  );
}

function TicketStepper({
  tickets,
  setTickets,
  setAllTickets,
  max,
}: {
  tickets: number;
  setTickets: (v: number) => void;
  setAllTickets: () => void;
  max: number | null;
}) {
  return (
    <div>
      <label className={labelClass}>
        Tickets <span className="text-red-500">*</span>
      </label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Fewer tickets"
          disabled={!max || tickets <= 1}
          onClick={() => setTickets(tickets - 1)}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-stone-300 text-stone-600 disabled:opacity-40 dark:border-stone-600 dark:text-stone-300"
        >
          <Minus size={14} />
        </button>
        <TicketCountInput tickets={tickets} setTickets={setTickets} max={max} />
        <button
          type="button"
          aria-label="More tickets"
          disabled={!max || tickets >= max}
          onClick={() => setTickets(tickets + 1)}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-stone-300 text-stone-600 disabled:opacity-40 dark:border-stone-600 dark:text-stone-300"
        >
          <Plus size={14} />
        </button>
        <span className="text-xs text-stone-400">of {max ?? '—'}</span>
        {max !== null && (
          <button
            type="button"
            onClick={setAllTickets}
            className="ml-auto text-xs font-semibold text-moss-600 hover:underline dark:text-moss-400"
          >
            All {max}
          </button>
        )}
      </div>
    </div>
  );
}

export function CreateBookingCustomerFields(props: CustomerFieldsProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass}>Full name</label>
          <input
            type="text"
            value={props.fullName}
            onChange={(e) => props.setFullName(e.target.value)}
            className={inputClass}
            placeholder="Jamie Rivera"
          />
        </div>
        <TicketStepper
          tickets={props.tickets}
          setTickets={props.setTickets}
          setAllTickets={props.setAllTickets}
          max={props.ticketsMax}
        />
      </div>
      <div>
        <div className="flex items-baseline justify-between">
          <label className={labelClass}>
            Email <span className="text-red-500">*</span>
          </label>
          <span className="text-xs text-stone-400">no phone collected</span>
        </div>
        <input
          type="email"
          value={props.email}
          onChange={(e) => props.setEmail(e.target.value)}
          className={inputClass}
          placeholder="jamie@email.com"
          required
        />
      </div>
    </div>
  );
}
